import { CharacterClassFromID } from "../classes";
import { NewFleet } from "../fleet";
import { NewParties, PartyMembers } from "../party";
import { defaultDefense, defaultShip } from "../types";
import type { Wave } from "../waves";
import type { Prefill } from "./prefill";
import { Settings } from "@/lib/trashsim/settings";

interface PrefillV1 {
    0: Array<PrefillV1Fleet>
    1: Array<PrefillV1Fleet>
    settings: PrefillV1Settings
}

interface PrefillV1Settings {
    debris_factor: number
    def_to_tF: boolean
    donut_galaxy: boolean
    donut_system: boolean
    galaxies: number
    global_deuterium_save_factor: number
    rapid_fire: number
    repair_factor: number
    speed_fleet: number
    systems: number
}

export interface PrefillV1Fleet {
    class: number
    planet: {
        galaxy: number
        system: number
        position: number
    }
    resources?: {
        metal: number,
        crystal: number,
        deuterium: number,
        food?: number
    }
    research?: { [key: string ]: {level: number} }
    ships?: { [key: string ]: {count: number} }
    defence?: { [key: string ]: {count: number} }
}

class PrefillV1Parser {
    raw: object

    constructor(raw: object) {
        this.raw = raw
    }

    load(): Prefill {
        if (!("0" in this.raw) || !("1" in this.raw)) {
            throw new Error('attackers or defenders not set')
        }

        let data = <PrefillV1>this.raw

        let wave = this.loadWave(data);
        let settings = this.loadSettings(data.settings);

        return {
            waves: [wave],
            settings: settings,
        }
    }

    loadWave(prefill: PrefillV1): Wave {
        const parties = <{[key: string]: PrefillV1Fleet[]}>{
            'attackers': prefill[0],
            'defenders': prefill[1]
        }

        let result = NewParties()
        for (const party of PartyMembers) {
            if (parties[party] == undefined) {
                continue
            }

            let fleetIndex = 0;
            for (const current of parties[party]) {
                const isDefender = party == 'defenders' && fleetIndex == 0;
                let fleet = NewFleet(isDefender);

                fleet.characterClass = CharacterClassFromID(current.class)
                fleet.coordinates = {
                    galaxy: current.planet.galaxy,
                    system: current.planet.system,
                    position: current.planet.position,
                }

                if (current.resources != undefined) {
                    fleet.resources = {
                        metal: current.resources.metal,
                        crystal: current.resources.crystal,
                        deuterium: current.resources.deuterium,
                        food: current.resources.food || 0,
                    }
                }

                if (current.ships != undefined) {
                    for (const e in current.ships) {
                        fleet.ships[e] = defaultShip()
                        fleet.ships[e].amount = current.ships[e].count
                    }
                }

                if (current.defence != undefined) {
                    for (const e in current.defence) {
                        fleet.defenses[e] = defaultDefense()
                        fleet.defenses[e].amount = current.defence[e].count
                    }
                }

                if (current.research != undefined) {
                    const presearch: {[key: string]: number} = {}
                    for (const key in current.research) {
                        presearch[key] = current.research[key].level
                    }

                    // FIXME add techs from old prefil
                    // fleet.techs = GetPrefillTechs(presearch)
                }

                result[party].fleets[fleetIndex] = fleet;
            };
            fleetIndex++;
        }

        return {
            parties: result,
            results: null,
            result: null
        }
    }

    loadSettings(prefill?: PrefillV1Settings): Settings | undefined {
        let settings = new Settings()

        if (prefill == undefined) {
            return undefined
        }

        settings.fleetToDebris = prefill.debris_factor * 100;
        settings.fleetWarSpeed = prefill.speed_fleet;
        settings.fleetHoldSpeed = prefill.speed_fleet;
        settings.donutGalaxy = Boolean(prefill.donut_galaxy);
        settings.donutSystem = Boolean(prefill.donut_system);
        settings.numberOfGalaxies = prefill.galaxies;
        settings.deuteriumSaveFactor = prefill.global_deuterium_save_factor;
        settings.defenseRepair = prefill.repair_factor * 100;

        if (prefill.def_to_tF) {
            settings.defenseToDebris = 100 - settings.defenseRepair;
        }

        return settings
    }
}

export {
    PrefillV1Parser
}